// @spec VUL-STR-001, VUL-STR-002, VUL-STR-007
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Settings2, Upload } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { RiskSecurityModuleContext } from '@/components/risk-security-module-context';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { useVulnerabilityGovernanceProfile } from '@/features/settings/vulnerabilities';
import {
  CreateVulnerabilityDialog,
  ExportVulnerabilitiesButton,
  ImportWizardDialog,
  useVulnerabilities,
  useVulnerabilitiesFiltersStore,
  VulnerabilitiesTable,
} from '@/features/vulnerabilities';
import type { VulnerabilityListItem } from '@/features/vulnerabilities/types';
import {
  getVulnerabilityFrameworkLabels,
  getVulnerabilityWorkflowType,
  isRecord,
} from '@/features/vulnerabilities/workflow-type';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

type VulnerabilityStatusFilter = 'all' | 'observed' | 'potential';
type VulnerabilityTypeFilter = 'all' | 'system' | 'custom';

// Filter options are initialized inside the component to access `t()`
const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'observed', label: 'Observed' },
  { value: 'potential', label: 'Potential' },
];

const TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Types' },
  { value: 'system', label: 'System' },
  { value: 'custom', label: 'Custom' },
];

function getVulnerabilityIdType(item: VulnerabilityListItem): 'system' | 'custom' {
  const customFields = isRecord(item.customFields) ? item.customFields : {};

  for (const key of ['id', 'vulnerabilityId', 'internalId', 'customId']) {
    const value = customFields[key];
    if (typeof value === 'string' && value.trim()) {
      return 'custom';
    }
  }

  return 'system';
}

export const Route = createFileRoute('/_authenticated/vulnerabilities/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.vulnerabilities'),
  }),
  component: VulnerabilitiesListPage,
});

/**
 * Vulnerabilities list page with comprehensive filtering, sorting, and pagination.
 *
 * Features:
 * - Paginated vulnerability list with severity/status/SLA badges
 * - Multi-select filters for severity, status, source, SLA status
 * - Text search across vulnerability title/CVE ID/description
 * - Sort by various fields in ascending/descending order
 * - Navigation to vulnerability detail page
 */
function VulnerabilitiesListPage() {
  const { t } = useTranslation('vulnerabilities');
  const [importOpen, setImportOpen] = React.useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [selectedVulnerabilityIds, setSelectedVulnerabilityIds] = React.useState<string[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<VulnerabilityStatusFilter>('all');
  const [vulnerabilityTypeFilter, setVulnerabilityTypeFilter] =
    React.useState<VulnerabilityTypeFilter>('all');
  const [tagsFilter, setTagsFilter] = React.useState('');
  const [frameworksFilter, setFrameworksFilter] = React.useState('');
  const navigate = useNavigate();
  const { organizations } = useOrganizations();
  const activeOrganizationId = organizations.find((org) => org.isActive)?.id ?? organizations[0]?.id ?? '';
  const { data: governanceProfile } = useVulnerabilityGovernanceProfile({
    enabled: Boolean(activeOrganizationId),
  });

  // Filter state from store
  const filters = useVulnerabilitiesFiltersStore.use.filters();
  const page = useVulnerabilitiesFiltersStore.use.page();
  const pageSize = useVulnerabilitiesFiltersStore.use.pageSize();
  const setPage = useVulnerabilitiesFiltersStore.use.setPage();
  const clearFilters = useVulnerabilitiesFiltersStore.use.clearFilters();

  const apiFilters = React.useMemo(
    () => ({
      ...filters,
      severity: null,
      status: null,
      source: null,
      slaStatus: null,
      isOnCisaKev: null,
      search: null,
      dateFrom: null,
      dateTo: null,
      sortBy: filters.sortBy === 'riskScore' ? ('discoveredAt' as const) : filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
    [filters]
  );

  // Fetch vulnerabilities with current filters
  const { vulnerabilities, isLoading, error, refetch } = useVulnerabilities({
    filters: apiFilters,
    page: 1,
    pageSize: 100,
  });
  const hasClientFilters =
    statusFilter !== 'all' ||
    vulnerabilityTypeFilter !== 'all' ||
    tagsFilter.trim().length > 0 ||
    frameworksFilter.trim().length > 0;

  const filteredVulnerabilities = React.useMemo(() => {
    const normalizedTagsFilter = tagsFilter.trim().toLowerCase();
    const normalizedFrameworksFilter = frameworksFilter.trim().toLowerCase();

    return vulnerabilities.filter((item) => {
      const workflowType = getVulnerabilityWorkflowType(item);
      if (statusFilter !== 'all' && workflowType !== statusFilter) {
        return false;
      }

      const idType = getVulnerabilityIdType(item);
      if (vulnerabilityTypeFilter !== 'all' && idType !== vulnerabilityTypeFilter) {
        return false;
      }

      if (normalizedTagsFilter) {
        const tags = item.tags ?? [];
        const matchesTag = tags.some((tag) => tag.toLowerCase().includes(normalizedTagsFilter));
        if (!matchesTag) {
          return false;
        }
      }

      if (normalizedFrameworksFilter) {
        const frameworkLabels = getVulnerabilityFrameworkLabels(item);
        const matchesFramework = frameworkLabels.some((label) =>
          label.toLowerCase().includes(normalizedFrameworksFilter)
        );
        if (!matchesFramework) {
          return false;
        }
      }

      return true;
    });
  }, [frameworksFilter, statusFilter, tagsFilter, vulnerabilities, vulnerabilityTypeFilter]);

  const localTotal = filteredVulnerabilities.length;
  const localTotalPages = Math.max(1, Math.ceil(localTotal / pageSize));

  const paginatedVulnerabilities = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredVulnerabilities.slice(start, start + pageSize);
  }, [filteredVulnerabilities, page, pageSize]);

  const visibleIdKey = paginatedVulnerabilities.map((item) => item.id).join(',');
  React.useEffect(() => {
    const visibleIds = new Set(visibleIdKey.split(',').filter(Boolean));
    setSelectedVulnerabilityIds((current) => current.filter((id) => visibleIds.has(id)));
  }, [visibleIdKey]);

  React.useEffect(() => {
    setPage(1);
  }, [frameworksFilter, statusFilter, tagsFilter, vulnerabilityTypeFilter, setPage]);

  React.useEffect(() => {
    if (page > localTotalPages) {
      setPage(localTotalPages);
    }
  }, [localTotalPages, page, setPage]);

  const handleToggleSelection = (vulnerabilityId: string, selected: boolean) => {
    setSelectedVulnerabilityIds((current) => {
      if (selected) {
        if (current.includes(vulnerabilityId)) {
          return current;
        }
        return [...current, vulnerabilityId];
      }
      return current.filter((id) => id !== vulnerabilityId);
    });
  };

  const handleToggleAllSelection = (vulnerabilityIds: string[], selected: boolean) => {
    setSelectedVulnerabilityIds((current) => {
      if (selected) {
        return Array.from(new Set([...current, ...vulnerabilityIds]));
      }
      const toRemove = new Set(vulnerabilityIds);
      return current.filter((id) => !toRemove.has(id));
    });
  };

  // Navigate to vulnerability detail
  const handleNavigate = (vulnerabilityId: string) => {
    void navigate({ to: '/vulnerabilities/$vulnerabilityId', params: { vulnerabilityId } });
  };

  const handleCreateSuccess = (vulnerability: { id: string }) => {
    void navigate({
      to: '/vulnerabilities/$vulnerabilityId',
      params: { vulnerabilityId: vulnerability.id },
    });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('listPage.title')}</h1>
            <p className="mt-1 font-medium text-foreground/70">
              {isLoading ? (
                t('listPage.loading')
              ) : (
                t('listPage.vulnerabilitiesFound', { count: localTotal })
              )}
            </p>
            {/*
              TODO(WP4-B): Render <GroupScopePill /> here once
              `VulnerabilityListResponse` (frontend types.ts) surfaces
              `includeSubsidiaries` / `includedOrganizationCount` from the
              backend (already present on the Pydantic schema via
              `RollupScopeMixin`). Skipped today because the frontend type
              does not mirror those fields yet. See
              `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
            */}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                void navigate({
                  to: '/settings/configration',
                  search: { aspect: 'vulnerabilities', section: 'attributes' },
                });
              }}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              {t('listPage.configAndGovernance')}
            </Button>
            <Button
              onClick={() => {
                setIsCreateDialogOpen(true);
              }}
            >
                <Plus className="mr-2 h-4 w-4" />
                {t('listPage.createVulnerability')}
            </Button>
            <ExportVulnerabilitiesButton filters={apiFilters} variant="outline" />
            <Button variant="outline" onClick={() => { setImportOpen(true); }}>
              <Upload className="mr-2 h-4 w-4" />
              {t('listPage.import')}
            </Button>
          </div>
        </header>

        <RiskSecurityModuleContext module="vulnerabilities" />

        <CreateVulnerabilityDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSuccess={handleCreateSuccess}
          orgId={activeOrganizationId}
        />

        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {governanceProfile?.status === 'published'
                ? t('listPage.guidelinesPublished', { version: String(governanceProfile.guidelinesVersion), date: new Date(governanceProfile.updatedAt).toLocaleString() })
                : t('listPage.noGuidelines')}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigate({
                    to: '/settings/configration',
                    search: { aspect: 'vulnerabilities', section: 'attributes' },
                  });
                }}
              >
                {t('listPage.openFields')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigate({
                    to: '/settings/configration',
                    search: { aspect: 'vulnerabilities', section: 'guidelines' },
                  });
                }}
              >
                {t('listPage.openGuidelines')}
              </Button>
            </span>
          </AlertDescription>
        </Alert>

        {/* Import Wizard Dialog (VUL-STR-007) */}
        <ImportWizardDialog
          isOpen={importOpen}
          onClose={() => { setImportOpen(false); }}
          organizationId={activeOrganizationId}
          onSuccess={() => void refetch()}
        />

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="mb-4 text-sm text-destructive">
              {error instanceof Error ? error.message : t('listPage.failedToLoad')}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('listPage.tryAgain')}
            </Button>
          </div>
        )}

        {/* Filters */}
        {!error && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
            <Select
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as VulnerabilityStatusFilter);
              }}
              className="h-9 w-40"
            />
            <Select
              options={TYPE_FILTER_OPTIONS}
              value={vulnerabilityTypeFilter}
              onChange={(value) => {
                setVulnerabilityTypeFilter(value as VulnerabilityTypeFilter);
              }}
              className="h-9 w-40"
            />
            <Input
              value={tagsFilter}
              onChange={(event) => {
                setTagsFilter(event.target.value);
              }}
              placeholder={t('listPage.tagsPlaceholder')}
              className="h-9 w-44"
            />
            <Input
              value={frameworksFilter}
              onChange={(event) => {
                setFrameworksFilter(event.target.value);
              }}
              placeholder={t('listPage.frameworksPlaceholder')}
              className="h-9 min-w-[240px] flex-1"
            />
            {hasClientFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setVulnerabilityTypeFilter('all');
                  setTagsFilter('');
                  setFrameworksFilter('');
                  clearFilters();
                }}
              >
                {t('listPage.clear')}
              </Button>
            )}
          </div>
        )}

        {/* Vulnerabilities table */}
        {!error && (
          <VulnerabilitiesTable
            vulnerabilities={paginatedVulnerabilities}
            isLoading={isLoading}
            total={localTotal}
            page={page}
            pageSize={pageSize}
            totalPages={localTotalPages}
            onPageChange={handlePageChange}
            onNavigate={handleNavigate}
            hasFilters={hasClientFilters}
            onClearFilters={() => {
              setStatusFilter('all');
              setVulnerabilityTypeFilter('all');
              setTagsFilter('');
              setFrameworksFilter('');
              clearFilters();
            }}
            selectedVulnerabilityIds={selectedVulnerabilityIds}
            onToggleVulnerabilitySelection={handleToggleSelection}
            onToggleAllVulnerabilities={handleToggleAllSelection}
            onCreateVulnerability={() => {
              setIsCreateDialogOpen(true);
            }}
            getWorkflowType={getVulnerabilityWorkflowType}
          />
        )}
      </div>
    </div>
  );
}
